/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {useState, useEffect} from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
} from "shards-react";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { accessCheck } from "../../utils/tools";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faPlusCircle, faBuilding } from '@fortawesome/free-solid-svg-icons';
import Select from "react-select";

import PageTitle from "../../components/common/PageTitle";
import ActivityIndicator from "../../components/common/ActivityIndicator";
import moment from "moment-timezone";

let defaultAvatar = require('../../images/blank_avatar.png')

const Locations = (props) => {

  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState('all');
  const [client, setClient] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [addedLocation, setAddedLocation] = useState(props.addedLocation);
  const [updatedLocation, setUpdatedLocation] = useState(props.updatedLocation);
  const [tempImage, setTempImage] = useState(null)
  const [showAppBanner, setShowAppBanner] = useState(true);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [locationToClone, setLocationToClone] = useState(null);
  
  // Add a style section with CSS for the location card hover effect
  const locationCardStyles = `
    .card-post {
      position: relative;
    }
    
    .card-post:hover {
      transform: translateY(-8px) scale(1.03);
      box-shadow: 0 16px 32px rgba(0,0,0,0.15);
    }
    
    .card-post:hover .location-card-overlay {
      border-color: #21AA73;
    }
    
    .filter-active .select__control {
      border-color: #21AA73 !important;
      border-width: 2px !important;
      box-shadow: 0 0 5px rgba(33, 170, 115, 0.2) !important;
      background-color: rgba(33, 170, 115, 0.05) !important;
    }
  `;
  
  useEffect(() => {
    props.getLiveLocations(props.user.id)
    document.title = 'Locations: EnforcePlus admin';
    !accessCheck(props.user, 'locations') && (window.location.href = '/') 
  }, [])

  useEffect(() => {
    setLocations(props.locations)
  }, [props.locations])

  // Add effect to respond to filter changes
  useEffect(() => {
    // When client or status changes, make sure we're working with the latest data
    if (props.locations?.length > 0) {
      setLocations(props.locations);
    }
  }, [client, status]);

  useEffect(() => {
    if (props.addedLocation) {
      setAddedLocation(true);
      setTimeout(() => {
        setAddedLocation(false)
        props.clearAddedLocation()
      }, 10000);
    }
    if (props.updatedLocation) {
      setUpdatedLocation(true);
      setTimeout(() => {
        setUpdatedLocation(false)
        props.clearAddedLocation()
      }, 10000);
    }
  }, [props.addedLocation, props.updatedLocation])

  const filterLocations = () => {
    let sortedLocations = locations;
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      sortedLocations = sortedLocations.filter(location => 
        (location.name && location.name.toLowerCase().includes(query)) || 
        (location.address1 && location.address1.toLowerCase().includes(query)) || 
        (location.address2 && location.address2.toLowerCase().includes(query)) || 
        (location.city && location.city.toLowerCase().includes(query)) ||
        (location.state && location.state.toLowerCase().includes(query)) ||
        (location.zip && location.zip.toLowerCase().includes(query))
      );
    }
    
    // Existing filters
    if (status !== 'all') {
      if (status === 'active') {
        sortedLocations = sortedLocations.filter(location => location.active)
      } else {
        sortedLocations = sortedLocations.filter(location => !location.active)
      }
    }
    if (client !== 'all') {
      sortedLocations = sortedLocations.filter(location => location.client === client)
    }
    sortedLocations = sortedLocations.sort((a, b) => a.name && b.name && a.name.localeCompare(b.name));
    return sortedLocations
  }

  const cloneLocation = (location) => {
    setLocationToClone(location);
    setShowCloneModal(true);
  }

  const handleConfirmClone = () => {
    if (locationToClone) {
      props.cloneLocation(locationToClone);
      setShowCloneModal(false);
    }
  }

  // Add this function to generate a color based on user name for consistency
  const generateAvatarColor = (name) => {
    // Array of complementary colors that work well with our green theme
    const colors = [
      '#21AA73', // primary green
      '#4A90E2', // blue
      '#F5A623', // orange/amber
      '#9B59B6', // purple
      '#E74C3C', // red
      '#3498DB', // light blue
      '#1ABC9C', // teal
      '#F39C12'  // yellow/gold
    ];
    
    if (!name) return colors[0]; // default to primary green if no name
    
    // Use a hash function to consistently map a name to a color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Function to get user initials
  const getInitials = (name) => {
    if (!name) return '?';
    
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <Container fluid className="main-content-container px-0">
      <style>{locationCardStyles}</style>
      {addedLocation && (
        <Alert className="mb-0" theme={'success'}>
          <i className="fa fa-info mx-2"></i> {props.location.name} has been successfully added!
        </Alert>
      )}
      {updatedLocation && (
        <Alert className="mb-0" theme={'success'}>
          <i className="fa fa-info mx-2"></i> {props.location.name} has been successfully updated!
        </Alert>
      )}
      <Container fluid className="main-content-container px-4">
        {/* Page Header */}
        <Row noGutters className="page-header py-4">
          <PageTitle xs="12" sm="4" title="Locations" className="text-sm-left" />
          <Col xs="12" className='text-right mt-4'>
            <Row className="align-items-center justify-content-end">
              {props.user.type === 'super' ? (
                <>
                  <Col md="4">
                    <div className="position-relative">
                      <i className="material-icons position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#66758F', fontSize: '20px' }}>search</i>
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          paddingLeft: '35px',
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#F8FAFC',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      />
                    </div>
                  </Col>
                  <Col md="3" className="px-2">
                    <Select
                      placeholder="Filter by client"
                      value={{ value: client, label: client === 'all' ? 'All Clients' : client }}
                      onChange={(selected) => setClient(selected.value)}
                      options={[
                        { value: 'all', label: 'All Clients' },
                        ...props.clients.map(client => ({
                          value: client.id,
                          label: client.name
                        }))
                      ]}
                      styles={{
                        control: (base) => ({
                          ...base,
                          height: '40px',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#FFFFFF',
                          '&:hover': {
                            border: '1px solid #E2E8F0'
                          }
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected ? '#21AA73' : state.isFocused ? '#F0FDF4' : 'white',
                          '&:active': {
                            backgroundColor: '#21AA73'
                          }
                        })
                      }}
                    />
                  </Col>
                  <Col md="3" className="px-2">
                    <Select
                      placeholder="Filter by status"
                      value={{ value: status, label: status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1) }}
                      onChange={(selected) => setStatus(selected.value)}
                      options={[
                        { value: 'all', label: 'All Status' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ]}
                      styles={{
                        control: (base) => ({
                          ...base,
                          height: '40px',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#FFFFFF',
                          '&:hover': {
                            border: '1px solid #E2E8F0'
                          }
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected ? '#21AA73' : state.isFocused ? '#F0FDF4' : 'white',
                          '&:active': {
                            backgroundColor: '#21AA73'
                          }
                        })
                      }}
                    />
                  </Col>
                  <Col md="2" className="pl-2">
                    <Link to="/locations/add">
                      <Button 
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          backgroundColor: '#21AA73',
                          border: 'none',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <i className="material-icons">add</i>
                        Add Location
                      </Button>
                    </Link>
                  </Col>
                </>
              ) : (
                <>
                  <Col md="8">
                    <div className="position-relative">
                      <i className="material-icons position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#66758F', fontSize: '20px' }}>search</i>
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          paddingLeft: '35px',
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#F8FAFC',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      />
                    </div>
                  </Col>
                  <Col md="4" className="pl-2">
                    <Link to="/locations/add">
                      <Button 
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          backgroundColor: '#21AA73',
                          border: 'none',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <i className="material-icons">add</i>
                        Add Location
                      </Button>
                    </Link>
                  </Col>
                </>
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <Col lg="3" md="6" sm="12" className="mb-4">
            <Link to={'/add-location'} style={{textDecoration: 'none'}}>
              <Card small className="card-post h-100" style={{
                borderRadius: '6px', 
                boxShadow: '0 6px 18px rgba(33, 170, 115, 0.15)', 
                border: '2px solid rgba(33, 170, 115, 0.3)', 
                transition: 'all 0.3s ease',
                backgroundColor: 'rgba(33, 170, 115, 0.03)',
                transform: 'scale(1.02)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.05)',
                  boxShadow: '0 16px 32px rgba(33, 170, 115, 0.2)',
                  borderColor: '#21AA73'
                }
              }}>
                <CardHeader className="border-bottom" style={{backgroundColor: 'rgba(33, 170, 115, 0.06)'}}>
                  <h6 className="m-0 font-weight-bold d-flex align-items-center" style={{color: '#21AA73'}}>
                    <FontAwesomeIcon icon={faPlusCircle} className="mr-2" style={{color: '#21AA73'}} />
                    Create New Location
                  </h6>
                </CardHeader>
                <CardBody style={{borderBottom: '1px solid rgba(33, 170, 115, 0.1)'}}>
                  {tempImage && (
                    <img src={`${tempImage}`} alt=""/>
                  )}
                  <p style={{color: '#495057', fontSize: 14, marginTop: '10px'}}>
                    Add a new location to start issuing citations or warnings.
                  </p>
                  <p style={{
                    color: '#21AA73', 
                    fontSize: 13, 
                    fontWeight: 500,
                    backgroundColor: 'rgba(33, 170, 115, 0.1)', 
                    padding: '8px 12px', 
                    borderRadius: '4px',
                    display: 'inline-block',
                    marginTop: '10px'
                  }}>
                    Get started immediately
                  </p>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col>
                      <span style={{color: '#21AA73', fontSize: 12, fontWeight: 600}}>CREATE LOCATION</span>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col className="text-center">
                      <Button 
                        tag={Link} 
                        to="/add-location"
                        theme="accent" 
                        size="md"
                        style={{
                          fontWeight: '500',
                          width: '100%',
                          padding: '10px',
                          boxShadow: '0 4px 12px rgba(33, 170, 115, 0.15)'
                        }}
                      >
                        <FontAwesomeIcon icon={faPlusCircle} className="mr-2" /> Create Location
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Link>
          </Col>
          
          {props.loadingLocations && props.locations.length === 0 ? (
            <Col className="mb-4 text-center py-5">
              <ActivityIndicator size="large" />
              <p className="mt-3 text-muted">Loading locations...</p>
            </Col>
          ) : filterLocations().length === 0 && props.loadedLocations ? (
            <Col className="mb-4 text-center py-5">
              <FontAwesomeIcon icon={faBuilding} size="2x" style={{color: '#21AA73', marginBottom: '10px', opacity: 0.8}} />
              <h6>No locations found</h6>
              <p style={{fontSize: '12px', color: '#9EA0A5'}}>Try changing your filters or add a new location.</p>
            </Col>
          ) : (
            filterLocations().map((location, idx) => (
              <Col lg="3" md="6" sm="12" className="mb-4" key={idx}>
                <Link to={'/permitted-vehicles/' + location.id} style={{textDecoration: 'none'}}>
                  <Card small className="card-post h-100" style={{
                    borderRadius: '6px', 
                    boxShadow: '0 1px 10px rgba(0,0,0,0.05)', 
                    border: '1px solid #eaedf3',
                    transition: 'all 0.3s ease',
                  }}>
                    <div 
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        borderRadius: '6px',
                        border: '2px solid transparent',
                        transition: 'all 0.3s ease',
                        pointerEvents: 'none',
                        zIndex: 1
                      }}
                      className="location-card-overlay"
                    />
                    <CardHeader className="border-bottom">
                      <h6 className="m-0 font-weight-bold d-flex align-items-center">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" style={{color: '#21AA73', opacity: 0.8}} />
                        {location.name}
                      </h6>
                      {location.active ? (
                        <span className="badge" style={{
                          backgroundColor: 'rgba(33, 170, 115, 0.1)',
                          color: '#21AA73',
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          marginTop: '6px'
                        }}>
                          Active
                        </span>
                      ) : (
                        <span className="badge" style={{
                          backgroundColor: 'rgba(108, 117, 125, 0.1)',
                          color: '#6c757d',
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          marginTop: '6px'
                        }}>
                          Inactive
                        </span>
                      )}
                      
                      {/* Zones indicator */}
                      {location.zones && location.zones.length > 0 && (
                        <span className="badge ml-1" style={{
                          backgroundColor: 'rgba(74, 144, 226, 0.1)',
                          color: '#4A90E2',
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          marginTop: '6px'
                        }}>
                          {location.zones.length} {location.zones.length === 1 ? 'Zone' : 'Zones'}
                        </span>
                      )}
                      
                      {/* Tenants indicator - check if tenants data is available */}
                      {location.tenants_count > 0 && (
                        <span className="badge ml-1" style={{
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          color: '#F5A623',
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          marginTop: '6px'
                        }}>
                          {location.tenants_count} {location.tenants_count === 1 ? 'Tenant' : 'Tenants'}
                        </span>
                      )}
                    </CardHeader>
                    <CardBody style={{borderBottom: '1px solid #EEEFF1'}}>
                      <p style={{color: '#9EA0A5', fontSize: 13}}>
                        {location.address1}<br />
                      {!!location.address2 && (
                        <>
                          {location.address2}<br />
                        </>
                      )}
                      {location.city}<br />
                        {location.state} {location.zip}<br />
                      </p>
                      {location.last_scan ? (
                        <p style={{
                          color: '#21AA73', 
                          fontSize: 12, 
                          backgroundColor: 'rgba(33, 170, 115, 0.05)', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          Last updated {moment(location.last_scan).fromNow()}
                        </p>
                      ) : (
                        <p style={{
                          color: '#9EA0A5', 
                          fontSize: 12, 
                          backgroundColor: 'rgba(108, 117, 125, 0.05)', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          No recent updates
                        </p>
                      )}
                    </CardBody>
                    <CardBody>
                      <Row>
                        <Col>
                          <span style={{color: '#818ea3', fontSize: 12, fontWeight: 500}}>LAST SCANNED BY</span>
                        </Col>
                        <Col xs={2} style={{fontSize: 16, color: '#818ea3'}} >
                          <Link 
                            className={'location-icon'} 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              cloneLocation(location);
                            }} 
                            to="#" 
                            style={{
                              textDecoration: 'none', 
                              color: '#818ea3',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#21AA73';
                              e.currentTarget.style.backgroundColor = 'rgba(33, 170, 115, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#818ea3';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </Link>
                        </Col>
                      </Row>
                      <Row className={'mt-2'}>
                        <Col className={'text-nowrap'}>
                          {!!location.last_scan_user ? (
                            <>
                              <div 
                                className="user-avatar rounded-circle mr-2" 
                                style={{
                                  height: 40, 
                                  width: 40, 
                                  border: '2px solid #f8f9fa',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  backgroundColor: generateAvatarColor(props.users.find(user => user.id === location.last_scan_user)?.name),
                                  color: '#fff',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: '500'
                                }}
                              >
                                {getInitials(props.users.find(user => user.id === location.last_scan_user)?.name)}
                              </div>
                              <span className="d-inline-block" style={{fontSize: 14, color: '#495057'}}>
                                {props.users.find(user => user.id === location.last_scan_user)?.name}
                              </span>
                            </>
                          ) : (
                            <>
                              <div 
                                className="user-avatar rounded-circle mr-2" 
                                style={{
                                  height: 40, 
                                  width: 40, 
                                  border: '2px solid #f8f9fa',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  backgroundColor: '#818ea3',
                                  color: '#fff',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: '500'
                                }}
                              >
                                ?
                              </div>
                              <span className="d-inline-block" style={{fontSize: 14, color: '#9EA0A5'}}>None recorded</span>
                            </>
                          )}
                        </Col>
                        <Col xs={2} style={{textDecoration: 'none', color: '#818ea3', paddingTop: 8}}>
                          <span
                            style={{
                              transition: 'all 0.2s ease',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#21AA73';
                              e.currentTarget.style.backgroundColor = 'rgba(33, 170, 115, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#818ea3';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                          <BorderColorIcon fontSize="inherit" />
                          </span>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Link>
              </Col>
            ))
          )}
        </Row>
      </Container>

      {/* Clone Location Confirmation Modal */}
      <Modal size="md" open={showCloneModal} toggle={() => setShowCloneModal(false)}>
        <ModalHeader toggle={() => setShowCloneModal(false)}>
          <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" style={{color: '#21AA73'}} />
          Clone Location
        </ModalHeader>
        <div className="modal-body">
          <div style={{ padding: '1rem' }}>
            <h6 style={{ color: '#495057', fontWeight: '600', marginBottom: '16px' }}>
              You are about to clone: <span style={{ color: '#21AA73' }}>{locationToClone?.name}</span>
            </h6>
            
            <div style={{ 
              backgroundColor: 'rgba(33, 170, 115, 0.05)', 
              padding: '16px', 
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid rgba(33, 170, 115, 0.1)'
            }}>
              <p style={{ fontSize: '14px', marginBottom: '10px', fontWeight: '600', color: '#495057' }}>
                The following will be cloned:
              </p>
              <ul style={{ paddingLeft: '20px', fontSize: '14px', color: '#495057' }}>
                <li>Location name (with "1" appended)</li>
                <li>Address details</li>
                <li>Active status</li>
                <li>Violation rules</li>
                <li>Officer alert messages</li>
                <li>Citation settings</li>
              </ul>
            </div>
            
            <div style={{ 
              backgroundColor: 'rgba(230, 73, 46, 0.05)', 
              padding: '16px', 
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid rgba(230, 73, 46, 0.1)'
            }}>
              <p style={{ fontSize: '14px', marginBottom: '10px', fontWeight: '600', color: '#495057' }}>
                The following will NOT be cloned:
              </p>
              <ul style={{ paddingLeft: '20px', fontSize: '14px', color: '#495057' }}>
                <li>Citation history</li>
                <li>Permitted vehicles (whitelist)</li>
                <li>Activity history</li>
                <li>Scan logs</li>
              </ul>
            </div>
            
            <p style={{ fontSize: '13px', color: '#818ea3', marginBottom: '20px', fontStyle: 'italic' }}>
              The new location will be created with a copy of the original name plus "1" appended.
              You can edit the name and other details after cloning.
            </p>
          </div>
        </div>
        <div className="modal-footer d-flex justify-content-between" style={{ padding: '1rem', borderTop: '1px solid #EEEFF1' }}>
          <Button 
            theme="light" 
            onClick={() => setShowCloneModal(false)}
            style={{ fontSize: '13px', color: '#495057', borderColor: '#dfe3e9' }}
          >
            Cancel
          </Button>
          <Button 
            theme="accent" 
            onClick={handleConfirmClone}
            style={{ fontSize: '13px', fontWeight: '500' }}
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
            Clone Location
          </Button>
        </div>
      </Modal>
    </Container>
  );
}

export default Locations;
